// Blog post editor — edit one post. M5, revised 2026-08-15.
//
// ⚠️ A POST IS SINGLE-LANGUAGE. "Language" in the sidebar sets `language_code`,
// which decides WHICH LISTING the post appears in (/blog vs /en/blog). There is
// no translation overlay and no AR/EN tabs anywhere on this screen — to publish
// the same article in both languages, write two posts.
//
// The body direction follows the post's language, so an Arabic post types RTL
// and an English one LTR.

import { Badge, Button, Card, Field, Input, Textarea } from '@fajrarabi/ui';
import { blogSlug, youtubeIdFrom, youtubeThumbnailUrl } from '@fajrarabi/zod-schemas/blog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '~/components';
import { ApiError } from '~/lib/api';
import {
  deletePost,
  getPost,
  listCategories,
  updatePost,
  uploadBlogImage,
  type BlogLocale,
} from '~/lib/blog';

// ⚠️ LAZY ON PURPOSE. TipTap + ProseMirror is ~130KB gzipped; importing it
// statically pushed the whole admin bundle over the <250KB initial-route budget
// in CLAUDE.md §5. Keep the lazy() if you touch this file.
const RichEditor = lazy(() => import('./-rich-editor').then((m) => ({ default: m.RichEditor })));

export const Route = createFileRoute('/_authed/blog/$id')({
  component: BlogEditorPage,
});

function BlogEditorPage() {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const postQuery = useQuery({
    queryKey: ['blog', 'post', id],
    queryFn: ({ signal }) => getPost(id, signal),
  });
  const categoriesQuery = useQuery({
    queryKey: ['blog', 'categories'],
    queryFn: ({ signal }) => listCategories(signal),
    staleTime: 30_000,
  });

  // Local draft. Seeded once from the server, then owned by the form.
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [languageCode, setLanguageCode] = useState<BlogLocale>('ar');
  const [categoryId, setCategoryId] = useState<string>('');
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isPublished, setIsPublished] = useState(false);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const featuredInput = useRef<HTMLInputElement>(null);
  const seeded = useRef(false);

  useEffect(() => {
    const post = postQuery.data;
    if (!post || seeded.current) return;
    seeded.current = true;
    setTitle(post.title);
    setSlug(post.slug);
    setExcerpt(post.excerpt ?? '');
    setContent(post.content ?? '');
    setLanguageCode(post.languageCode);
    setCategoryId(post.categoryId ?? '');
    setFeaturedImage(post.featuredImage);
    setYoutubeUrl(post.youtubeUrl ?? '');
    setIsPublished(post.isPublished);
  }, [postQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      updatePost(id, {
        title: title.trim(),
        slug: slug.trim(),
        excerpt: excerpt.trim() || null,
        content: content || null,
        featuredImage,
        // '' clears youtube_url to null server-side (§5.2).
        youtubeUrl: youtubeUrl.trim(),
        categoryId: categoryId || null,
        languageCode,
        isPublished,
      }),
    onSuccess: (post) => {
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      qc.setQueryData(['blog', 'post', id], post);
      setSaveError(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save.');
      setSaved(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deletePost(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      void navigate({ to: '/blog' });
    },
  });

  async function onPickFeatured(file: File) {
    setUploading(true);
    try {
      // Upload-first: the URL comes back with no DB row, so this works even on
      // a post whose other edits are not saved yet.
      setFeaturedImage(await uploadBlogImage(file));
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  }

  if (postQuery.isLoading) return <p className="p-4 text-sm text-slate-500">Loading…</p>;
  if (postQuery.isError || !postQuery.data) {
    return (
      <p role="alert" className="p-4 text-sm text-rose-700">
        Could not load this post.
      </p>
    );
  }

  const slugValid = blogSlug.safeParse(slug.trim()).success;
  const videoId = youtubeIdFrom(youtubeUrl);
  const youtubeInvalid = youtubeUrl.trim() !== '' && !videoId;
  const publicPath = `/${languageCode === 'en' ? 'en/' : ''}blog/${slug}`;
  // The post's language drives the body direction — an Arabic article must type
  // RTL inside the otherwise-LTR admin.
  const bodyDir = languageCode === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="mx-auto max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Button variant="ghost" onClick={() => void navigate({ to: '/blog' })}>
            ← All posts
          </Button>
          <h1 className="mt-1 truncate text-2xl font-semibold text-slate-900">
            {title || 'Untitled post'}
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            <code className="rounded bg-slate-100 px-1">{publicPath}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <Badge tone="success">Saved</Badge>}
          <Badge tone={isPublished ? 'success' : 'neutral'}>
            {isPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      </header>

      {saveError && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
          {saveError}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* ── Main column ─────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-6">
          <Card>
            <div className="p-4">
              <Field label="Title">
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    dir={bodyDir}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Slug"
                className="mt-4"
                hint="Auto-filled from the title when the post was created. Changing it breaks existing links."
                error={
                  slug.trim() && !slugValid
                    ? 'Lowercase letters, Arabic script, digits and hyphens only.'
                    : undefined
                }
              >
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    dir="auto"
                  />
                )}
              </Field>

              <Field
                label="Excerpt"
                className="mt-4"
                hint="Shown on the listing card and used as the meta description."
              >
                {(fieldId) => (
                  <Textarea
                    id={fieldId}
                    dir={bodyDir}
                    rows={3}
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                  />
                )}
              </Field>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Body</h2>
              <Suspense
                fallback={
                  <div className="min-h-[24rem] rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
                    Loading editor…
                  </div>
                }
              >
                <RichEditor dir={bodyDir} value={content} onChange={setContent} />
              </Suspense>
            </div>
          </Card>
        </div>

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <aside className="flex flex-col gap-6">
          <Card>
            <div className="p-4">
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="h-4 w-4"
                />
                Published
              </label>
              <p className="mt-1 text-xs text-slate-500">
                {postQuery.data.publishedAt
                  ? `First published ${new Date(postQuery.data.publishedAt).toLocaleDateString('en-GB')}. Re-publishing keeps that date.`
                  : 'Publishing stamps the date once; unpublishing later keeps it.'}
              </p>

              <Button
                className="mt-4 w-full"
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending || !title.trim() || !slugValid || youtubeInvalid}
              >
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <Field
                label="Language"
                hint="Decides which listing the post appears in. A post lives in one language only."
              >
                {(fieldId) => (
                  <select
                    id={fieldId}
                    value={languageCode}
                    onChange={(e) => setLanguageCode(e.target.value as BlogLocale)}
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  >
                    <option value="ar">Arabic — shows on /blog</option>
                    <option value="en">English — shows on /en/blog</option>
                  </select>
                )}
              </Field>

              <Field label="Category" className="mt-4">
                {(fieldId) => (
                  <select
                    id={fieldId}
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  >
                    <option value="">No category</option>
                    {(categoriesQuery.data ?? []).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <h2 className="text-sm font-semibold text-slate-900">Featured image</h2>
              {featuredImage ? (
                <div className="mt-3">
                  <img
                    src={featuredImage}
                    alt=""
                    className="aspect-video w-full rounded-md object-cover"
                  />
                  <Button variant="ghost" className="mt-2" onClick={() => setFeaturedImage(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500">No image set.</p>
              )}
              <input
                ref={featuredInput}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void onPickFeatured(file);
                  e.target.value = '';
                }}
              />
              <Button
                variant="secondary"
                className="mt-3 w-full"
                disabled={uploading}
                onClick={() => featuredInput.current?.click()}
              >
                {uploading ? 'Uploading…' : featuredImage ? 'Replace image' : 'Upload image'}
              </Button>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <Field
                label="YouTube URL"
                hint="When set, the video replaces the featured image on the post page."
                error={youtubeInvalid ? 'Not a recognisable YouTube URL.' : undefined}
              >
                {/* dir=ltr: a URL must not reorder next to Arabic copy. */}
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    dir="ltr"
                    value={youtubeUrl}
                    placeholder="https://www.youtube.com/watch?v=…"
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                  />
                )}
              </Field>
              {videoId && (
                <img
                  src={youtubeThumbnailUrl(videoId)}
                  alt=""
                  className="mt-3 aspect-video w-full rounded-md object-cover"
                />
              )}
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <Button
                variant="ghost"
                className="w-full text-error-700"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete post
              </Button>
            </div>
          </Card>

          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete this post"
            description="This post is removed permanently and cannot be recovered from here."
            confirmLabel="Delete post"
            tone="danger"
            onConfirm={async () => {
              await deleteMut.mutateAsync();
            }}
          />
        </aside>
      </div>
    </div>
  );
}
